import { ReactNode } from 'react';
import { IN_ARTICLE_AD_PROVIDERS } from './zone';
import { InContentAd } from './inContentAd';
import { Upsell } from '../upsells/index';

/** Number of rows between each interstitial slot */
export const LEADERBOARD_AD_INTERVAL = 20;

/** Maximum number of interstitial slots per page */
export const MAX_LEADERBOARD_INTERSTITIALS = 4;

/** Default pattern: 75% ads, 25% upsells */
export const DEFAULT_LEADERBOARD_AD_PATTERN: ('ad' | 'upsell')[] = ['ad', 'ad', 'upsell', 'ad'];

/**
 * Determines whether a given slot should show an ad or an upsell.
 * Uses stable randomization via seed so the pattern is consistent per page load.
 */
export function getLeaderboardSlotType(
  slotIndex: number,
  seed: number,
  pattern: ('ad' | 'upsell')[] = DEFAULT_LEADERBOARD_AD_PATTERN
): 'ad' | 'upsell' {
  return pattern[(seed + slotIndex) % pattern.length];
}

/**
 * Returns the position of this upsell within the page's upsell sequence.
 * Ad slots do not consume a position.
 */
export function getLeaderboardUpsellSequenceIndex(
  slotIndex: number,
  seed: number,
  precedingUpsells = 0,
  pattern: ('ad' | 'upsell')[] = DEFAULT_LEADERBOARD_AD_PATTERN
): number {
  let sequenceIndex = precedingUpsells;

  for (let index = 0; index < slotIndex; index++) {
    if (getLeaderboardSlotType(index, seed, pattern) === 'upsell') {
      sequenceIndex++;
    }
  }

  return sequenceIndex;
}

/**
 * Returns the position of the upsell below a leaderboard. Ad-free pages have
 * a top upsell; ad-supported pages may have upsells in interstitial slots.
 */
export function getLeaderboardBottomUpsellSequenceIndex(
  totalRows: number,
  currentFeatures: { no_ads?: boolean },
  siteConfig: { displayAdsProvider?: string },
  seed: number,
  pattern: ('ad' | 'upsell')[] = DEFAULT_LEADERBOARD_AD_PATTERN
): number {
  if (currentFeatures.no_ads) {
    return 1;
  }

  let precedingUpsells = 0;

  for (let slotIndex = 0; slotIndex < MAX_LEADERBOARD_INTERSTITIALS; slotIndex++) {
    const rowIndex = (slotIndex + 1) * LEADERBOARD_AD_INTERVAL;
    if (
      shouldShowLeaderboardInterstitial(rowIndex, totalRows, currentFeatures, siteConfig) !== false &&
      getLeaderboardSlotType(slotIndex, seed, pattern) === 'upsell'
    ) {
      precedingUpsells++;
    }
  }

  return precedingUpsells;
}

/**
 * Returns the interstitial slot index (0-based) if an interstitial should be shown
 * before the row at `rowIndex`, or `false` if not.
 */
export function shouldShowLeaderboardInterstitial(
  rowIndex: number,
  totalRows: number,
  currentFeatures: { no_ads?: boolean },
  siteConfig: { displayAdsProvider?: string }
): number | false {
  if (currentFeatures.no_ads) {
    return false;
  }
  if (!IN_ARTICLE_AD_PROVIDERS.includes(siteConfig.displayAdsProvider || '')) {
    return false;
  }
  if (rowIndex === 0 || rowIndex % LEADERBOARD_AD_INTERVAL !== 0) {
    return false;
  }
  const slotIndex = Math.floor(rowIndex / LEADERBOARD_AD_INTERVAL) - 1;
  if (slotIndex >= MAX_LEADERBOARD_INTERSTITIALS) {
    return false;
  }
  if (totalRows <= rowIndex) {
    return false;
  }
  return slotIndex;
}

/**
 * Renders the content for a leaderboard interstitial slot (ad or upsell).
 */
export function LeaderboardInterstitialContent({
  slotIndex,
  seed,
  precedingUpsells = 1
}: {
  slotIndex: number;
  seed: number;
  precedingUpsells?: number;
}): ReactNode {
  const slotType = getLeaderboardSlotType(slotIndex, seed);
  if (slotType === 'upsell') {
    return (
      <Upsell
        source="interstitial"
        seedOffset={getLeaderboardUpsellSequenceIndex(slotIndex, seed, precedingUpsells)}
        fallback={<InContentAd />}
      />
    );
  }
  return <InContentAd />;
}

/**
 * Renders a `<tr>` containing a leaderboard interstitial (ad or upsell).
 * Use in `<table>`-based ranking components.
 */
export function LeaderboardInterstitialRow({
  slotIndex,
  seed,
  colSpan,
  precedingUpsells = 1
}: {
  slotIndex: number;
  seed: number;
  colSpan: number;
  precedingUpsells?: number;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="slds-text-align--center slds-p-around--x-small rio-simple-table-cell">
        <div className="rio-simple-table-cell__inner">
          <LeaderboardInterstitialContent slotIndex={slotIndex} seed={seed} precedingUpsells={precedingUpsells} />
        </div>
      </td>
    </tr>
  );
}
